import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { useEffect, useState } from "react";
import validateEmail from "../../utilities/validateEmail";
import { NotificationManager } from 'react-notifications';
import getLocations from "../../apis/getLocations/getLocations";
import fetchCountryStateCity from "../../apis/fetchCountryStateCity";
import RequiredFieldRenderer from "./SubRenderFields";
import candidateSubmissionConfig from "../../utilities/candidateSubmissionConfig"

const SubmissionComp = ({
  setSubmitDataFromModal,
  selectedCandidate,
  setSelectedCandidate,
  reqDetailData,
  candidateSubmissionField,
  sharedAdditionalSpoc,
  setSharedAdditionalSpoc,
  onAdditionalSpocRequirementChange,
  onSubmissionConfigStatusChange,
  onFormatSubmissionData,
  currencyOptions, 
  setCurrencyOptions
}) => {

  const [requiredFields, setRequireFields] = useState([]);
  const [additionalSpocRequired, setAdditionalSpocRequired] = useState(false);
  const [additionalSpocOptions, setAdditionalSPOCOptions] = useState([]);
  const [currentAdditionalSpocValue, setCurrentAdditionalSpocValue] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [disabledFields, setDisabledFields] = useState(false);
  const [noActiveConfigMessage, setNoActiveConfigMessage] = useState("");

  // Location search states
  const [locationSearchText, setLocationSearchText] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationList, setLocationList] = useState([]);

  // Preferred locations search states
  const [preferredLocationSearchText, setPreferredLocationSearchText] = useState("");
  const [preferredLocationLoading, setPreferredLocationLoading] = useState(false);
  const [preferredLocationList, setPreferredLocationList] = useState([]);

  // Clients worked with states
  const [clientsWorkedWithOptions, setClientsWorkedWithOptions] = useState([]);
  const [currentClientsWorkedWithValue, setCurrentClientsWorkedWithValue] = useState("");
  

  const [fieldAltNames] = useState({
    CSF01: "name",
    CSF02: "emailID",
    CSF03: "phoneNumber",
    CSF08: "currentLocation",
    CSF04: "yr_of_exp",
    CSF05: "relevantExperience",
    CSF06: "primarySkill",
    CSF07: "clientsWorkedWith",
    CSF09: "noticePeriod",
    CSF10: "candidateSynopsis",
    CSF11: "currentSalary",
    CSF12: "expectedSalary",
    CSF13: "preferdlocation",
    CSF14: "currentHourlyRate",
  });

  const allowedSubmissionCodes = [
    candidateSubmissionConfig.currenthourlyrate,
    candidateSubmissionConfig.currentdailyrate,
    candidateSubmissionConfig.currentmonthlyrate,
    candidateSubmissionConfig.currentyearlyrate,
    candidateSubmissionConfig.expectedhourlyrate,
    candidateSubmissionConfig.expecteddailyrate,
    candidateSubmissionConfig.expectedmonthlyrate,
    candidateSubmissionConfig.expectedyearlyrate
  ];

  useEffect(() => {
  async function fetchData() {
    
    if (currencyOptions.length > 0) {
      return; 
    }

    const payload = {
      actionName: "country"
    };

    try {
      const response = await fetchCountryStateCity(payload);
      const currencies = response.data.data.map(country => country.currency);
      setCurrencyOptions(currencies);
    } catch (error) {
      console.error("Error fetching country/state/city:", error);
    }
  }

  fetchData();
}, []); 

  const getRequiredFields = () => {
    if (!reqDetailData || !candidateSubmissionField) {
      return;
    }

    if (!Array.isArray(candidateSubmissionField)) {
      console.error('candidateSubmissionField is not an array:', candidateSubmissionField);
      return;
    }

    let placementTemplateCode = reqDetailData?.placementTemplateCode || "";
    let submissionFields = [];
    let shouldSetAdditionalSpocRequired = false;

    let spocConfig = reqDetailData?.spocData?.spocSubmissionConfig || [];
    spocConfig = spocConfig.filter(item => item.placementTemplateCode === placementTemplateCode);

    let clientConfig = reqDetailData?.clientData?.clientSubmissionConfig || [];
    clientConfig = clientConfig.filter(item => item.placementTemplateCode === placementTemplateCode);

    let activeSpocConfig = spocConfig.filter(item => item.isActive === true);
    let activeClientConfig = clientConfig.filter(item => item.isActive === true);

    const isSpocActiveWithEmail = reqDetailData?.spocData?.isEmailRequired && reqDetailData?.spocData?.isActive;
    const isClientActiveWithEmail = reqDetailData?.clientData?.isEmailRequired && reqDetailData?.clientData?.isActive;

    const hasAnyActiveConfig = activeSpocConfig.length > 0 || activeClientConfig.length > 0;

    if (!hasAnyActiveConfig) {
      submissionFields = [];
      const templateName = placementTemplateCode === "PC01" ? "permanent" : placementTemplateCode === "PC02" ? "contractual" : placementTemplateCode;

      // For contractual/permanent requisitions without template, allow submission
      setNoActiveConfigMessage(`No ${templateName} template configured. Proceeding with submission for ${templateName} requisition.`);
      if (onSubmissionConfigStatusChange) {
        onSubmissionConfigStatusChange(true);
      }
    }
    else if (isSpocActiveWithEmail && activeSpocConfig.length > 0) {
      submissionFields = activeSpocConfig[0]?.submissionField || [];
      setNoActiveConfigMessage("");
      if (onSubmissionConfigStatusChange) {
        onSubmissionConfigStatusChange(true);
      }
    }
    else if (isSpocActiveWithEmail && activeSpocConfig.length === 0 && isClientActiveWithEmail && activeClientConfig.length > 0) {
      submissionFields = activeClientConfig[0]?.submissionField || [];
      setNoActiveConfigMessage("");
      shouldSetAdditionalSpocRequired = true;
      if (onSubmissionConfigStatusChange) {
        onSubmissionConfigStatusChange(true);
      }
    }
    else if (!isSpocActiveWithEmail && isClientActiveWithEmail && activeClientConfig.length > 0) {
      submissionFields = activeClientConfig[0]?.submissionField || [];
      setNoActiveConfigMessage("");
      shouldSetAdditionalSpocRequired = true;
      if (onSubmissionConfigStatusChange) {
        onSubmissionConfigStatusChange(true);
      }
    }
    else {
      submissionFields = [];
      if (onSubmissionConfigStatusChange) {
        onSubmissionConfigStatusChange(false);
      }
    }

    let requiredFieldsArr = [];
    submissionFields.forEach(field => {
      let fieldData = candidateSubmissionField.find(item => item.submissionCode === field.submissionCode && field.isFieldActive === true);
      if (field.isFieldActive && fieldData) {
        requiredFieldsArr.push(fieldData);
      }
    });

    const hasSubmissionData = selectedCandidate.submitData?.candidateSubmissionData &&
      Object.keys(selectedCandidate.submitData.candidateSubmissionData).length > 0 &&
      selectedCandidate.submitData.candidateSubmissionData.details &&
      selectedCandidate.submitData.candidateSubmissionData.details.length > 0;

    // Initialize submission data if it doesn't exist or is empty
    if (!hasSubmissionData && !isInitialized && selectedCandidate.submitData?.email && requiredFieldsArr.length > 0) {
      let resume = selectedCandidate?.submitData?.candidateCompleteResume || {};

      setSubmitDataFromModal(prevSubmitData => {
        const submitDataFromModalCopy = [...prevSubmitData];
        const index = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);

        if (index === -1) {
          return prevSubmitData;
        }

        let submissionFieldsData = {};
        let requiredData = [];
        requiredFieldsArr.forEach(field => {
          if (!field || !field.submissionCode) return;

          let obj = {
            "subCode": field.submissionCode || "",
          };
          let altName = fieldAltNames[field.submissionCode];
          let value = "";
          if (altName === "currentLocation") {
            let [city, state, country] = [resume.city, resume.state, resume.country];
            value = {
              city: city || "",
              state: state || "",
              country: country || ""
            };
          }
          else if (altName === "preferdlocation") {
            value = [];
          }
          else if (altName === "clientsWorkedWith") {
            value = [];
          }
          else if (altName === 'yr_of_exp' || altName === 'relevantExperience') {
            const rv = resume[altName];
            if (rv && typeof rv === 'object') {
              const y = rv.years ?? rv.year ?? rv.y ?? '';
              const m = rv.months ?? rv.month ?? rv.m ?? '';
              value = {
                years: y === '' ? 0 : parseInt(y) || 0,
                months: m === '' ? 0 : parseInt(m) || 0
              };
            } else if (rv !== undefined && rv !== null && rv !== '') {
              if (typeof rv === 'number') {
                const years = Math.floor(rv);
                const months = Math.round((rv - years) * 12);
                value = { years, months };
              } else {
                const years = parseInt(rv) || 0;
                value = { years, months: 0 };
              }
            } else {
              value = { years: 0, months: 0 };
            }
          }
          else if (altName === 'phoneNumber') {
            const phoneValue = resume[altName] || "";

            if (phoneValue && typeof phoneValue === 'string' && phoneValue.trim()) {
              // More comprehensive regex to handle various phone number formats
              const phoneMatch = phoneValue.match(/^(\+?\d{1,4})[-\s]?(.+)$/);

              if (phoneMatch && phoneMatch[2]) {
                const countryCode = phoneMatch[1].startsWith('+') ? phoneMatch[1] : `+${phoneMatch[1]}`;
                const phoneNumber = phoneMatch[2].replace(/[-\s()[\]]/g, ''); // Clean the phone number

                // Validate that we have a meaningful phone number
                if (phoneNumber.length >= 7) { // Minimum phone number length
                  submitDataFromModalCopy[index].candidateCompleteResume = submitDataFromModalCopy[index].candidateCompleteResume || {};
                  submitDataFromModalCopy[index].candidateCompleteResume.countryCallingCode = countryCode;
                  submitDataFromModalCopy[index].countryCallingCode = countryCode;

                  value = phoneNumber;
                } else {
                  // If parsed phone number is too short, keep original value
                  value = phoneValue;
                }
              } else if (phoneValue.startsWith('+')) {
                // Handle cases where entire number is with country code but no separator
                const cleanNumber = phoneValue.replace(/[-\s()[\]]/g, '');
                if (cleanNumber.length > 4) { // Assuming country code + minimum 4 digits
                  // Extract likely country code (1-4 digits after +)
                  const countryCodeMatch = cleanNumber.match(/^\+(\d{1,4})/);
                  if (countryCodeMatch) {
                    const countryCode = `+${countryCodeMatch[1]}`;
                    const phoneNumber = cleanNumber.substring(countryCode.length);

                    submitDataFromModalCopy[index].candidateCompleteResume = submitDataFromModalCopy[index].candidateCompleteResume || {};
                    submitDataFromModalCopy[index].candidateCompleteResume.countryCallingCode = countryCode;
                    submitDataFromModalCopy[index].countryCallingCode = countryCode;

                    value = phoneNumber;
                  } else {
                    value = phoneValue;
                  }
                } else {
                  value = phoneValue;
                }
              } else {
                // No country code detected, use default (+91) and keep the number as is
                const cleanNumber = phoneValue.replace(/[-\s()[\]]/g, '');
                if (cleanNumber.length >= 7) {
                  submitDataFromModalCopy[index].candidateCompleteResume = submitDataFromModalCopy[index].candidateCompleteResume || {};
                  submitDataFromModalCopy[index].candidateCompleteResume.countryCallingCode = "+91"; // Default country code
                  submitDataFromModalCopy[index].countryCallingCode = "+91";

                  value = cleanNumber;
                } else {
                  value = phoneValue;
                }
              }
            } else {
              value = phoneValue;
            }
          }

          else if (field.submissionCode === candidateSubmissionConfig.currentCTC || field.submissionCode === candidateSubmissionConfig.expectedCTC) {
            value = {
              amount: resume[altName] || "",
              currency: resume?.currency || "",
              unit: "per year"
            };
          }
          else if (allowedSubmissionCodes.includes(field.submissionCode)) {
            let unit = '';
            const fieldSubCode = field.submissionCode;
            if ([candidateSubmissionConfig.currenthourlyrate, candidateSubmissionConfig.expectedhourlyrate].includes(fieldSubCode)) {
              unit = 'per hour';
            } else if ([candidateSubmissionConfig.currentdailyrate, candidateSubmissionConfig.expecteddailyrate].includes(fieldSubCode)) {
              unit = 'per day';
            } else if ([candidateSubmissionConfig.currentmonthlyrate, candidateSubmissionConfig.expectedmonthlyrate].includes(fieldSubCode)) {
              unit = 'per month';
            } else if ([candidateSubmissionConfig.currentyearlyrate, candidateSubmissionConfig.expectedyearlyrate].includes(fieldSubCode)) {
              unit = 'per year';
            }
            value = {
              amount: resume[altName] || "",
              currency: "",
              unit: unit
            };
          }
          else {
            value = resume[altName] || "";
          }
          obj.value = value;
          requiredData.push(obj);
        });
        submissionFieldsData.details = requiredData;

        if (!selectedCandidate.submitData?.candidateReqExist) {
          submissionFieldsData.altContacts = sharedAdditionalSpoc || [];
        } else {
          submissionFieldsData.altContacts = selectedCandidate.submitData?.candidateSubmissionData?.altContacts || [];
        }
        submitDataFromModalCopy[index].candidateSubmissionData = submissionFieldsData;

        setSelectedCandidate(prevCandidate => ({
          ...prevCandidate,
          submitData: submitDataFromModalCopy[index]
        }));

        return submitDataFromModalCopy;
      });

      setIsInitialized(true);
    }

    setRequireFields(requiredFieldsArr);
    if (shouldSetAdditionalSpocRequired) {
      setAdditionalSpocRequired(true);
      if (onAdditionalSpocRequirementChange) onAdditionalSpocRequirementChange(true);
    } else {
      setAdditionalSpocRequired(false);
      if (onAdditionalSpocRequirementChange) onAdditionalSpocRequirementChange(false);
    }
  };

  const validateField = (subCode, value) => {
  const fieldDef = candidateSubmissionField?.find(f => f.submissionCode === subCode);
  if (!fieldDef) return { isValid: true, error: null };

  const { dataType, name } = fieldDef;

  // Handle empty/null values
  if (!value && value !== 0 && value !== false) {
    return { isValid: false, error: `${name} is required` };
  }

  switch (dataType) {
    case "String":
      if (subCode === candidateSubmissionConfig.emailID) {
        return {
          isValid: validateEmail(value),
          error: validateEmail(value) ? null : "Invalid email format"
        };
      }
      if (subCode === candidateSubmissionConfig.contactNo) {
        const phoneRegex = /^\+\d{1,4}\d{7,14}$/;
        return {
          isValid: phoneRegex.test(value),
          error: phoneRegex.test(value) ? null : "Invalid phone number format"
        };
      }
      return {
        isValid: typeof value === 'string' && value.trim().length > 0,
        error: typeof value === 'string' && value.trim().length > 0 ? null : `${name} cannot be empty`
      };

    case "Number":
      if (subCode === candidateSubmissionConfig.totalExperience || subCode === candidateSubmissionConfig.relevantExperience) {
        if (typeof value === "object" && value !== null) {
          const years = parseInt(value.years);
          const months = parseInt(value.months);
          const isValidYears = !isNaN(years) && years >= 0;
          const isValidMonths = !isNaN(months) && months >= 0 && months <= 11;
          return {
            isValid: isValidYears && isValidMonths,
            error: isValidYears && isValidMonths ? null : "Invalid experience format"
          };
        }
      }
      const numValue = parseFloat(value);
      return {
        isValid: !isNaN(numValue) && numValue >= 0,
        error: !isNaN(numValue) && numValue >= 0 ? null : `${name} must be a valid positive number`
      };

    case "Object":
      if (subCode === candidateSubmissionConfig.currentLocation) {
        return {
          isValid: value && typeof value === "object" && value.country && value.country.trim(),
          error: value && typeof value === "object" && value.country && value.country.trim() ? null : "Country is required"
        };
      }
      if (allowedSubmissionCodes.includes(subCode) || 
          subCode === candidateSubmissionConfig.currentCTC || 
          subCode === candidateSubmissionConfig.expectedCTC) {
        const hasAmount = value.amount && !isNaN(parseFloat(value.amount)) && parseFloat(value.amount) > 0;
        const hasCurrency = value.currency && value.currency.trim();
        const hasUnit = value.unit && value.unit.trim();
        return {
          isValid: hasAmount && hasCurrency && hasUnit,
          error: hasAmount && hasCurrency && hasUnit ? null : null
        };
      }
      return { isValid: true, error: null };

    case "Array":
      if (!Array.isArray(value)) {
        return { isValid: false, error: `${name} must be an array` };
      }
      if (subCode === candidateSubmissionConfig.clientsWorkedWith) {
        const validItems = value.filter(item => item && typeof item === 'string' && item.trim());
        return {
          isValid: validItems.length > 0,
          error: validItems.length > 0 ? null : "At least one valid client is required"
        };
      }
      if (subCode === candidateSubmissionConfig.preferredLocation) {
        const validLocations = value.filter(loc => 
          loc && typeof loc === 'object' && loc.country && loc.country.trim()
        );
        return {
          isValid: validLocations.length > 0,
          error: validLocations.length > 0 ? null : "At least one valid preferred location is required"
        };
      }
      return { isValid: true, error: null };

    default:
      return { isValid: true, error: null };
  }
};

  const handleFieldChange = (subCode, value) => {
    const validation = validateField(subCode, value);
    // if (!validation.isValid) {
    //   NotificationManager.warning(validation.error, "Validation Error", 3000);
    // }

    setSubmitDataFromModal(prevSubmitData => {
      const submitDataFromModalCopy = [...prevSubmitData];
      const index = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);

      if (index === -1) return prevSubmitData;

      const details = submitDataFromModalCopy[index].candidateSubmissionData.details || [];
      const fieldIndex = details.findIndex(item => item.subCode === subCode);

      if (fieldIndex !== -1) {
        if (allowedSubmissionCodes.includes(subCode) &&
          details[fieldIndex].value && typeof details[fieldIndex].value === 'object') {
          const currentUnit = details[fieldIndex].value.unit;
          if (currentUnit && typeof value === 'object') {
            value.unit = currentUnit;
          }
        }
        details[fieldIndex].value = value;
      } else {
        details.push({ subCode, value });
      }

      if (allowedSubmissionCodes.includes(subCode)) {
        for (let rateFieldCode of allowedSubmissionCodes) {
          const rateFieldIndex = details.findIndex(item => item.subCode === rateFieldCode);
          if (rateFieldIndex !== -1 && details[rateFieldIndex].value && typeof details[rateFieldIndex].value === 'object') {
            details[rateFieldIndex].value = {
              ...details[rateFieldIndex].value,
              currency: value.currency
            };
          }
        }
      }

      submitDataFromModalCopy[index].candidateSubmissionData.details = details;

      setSelectedCandidate(prevCandidate => ({
        ...prevCandidate,
        submitData: submitDataFromModalCopy[index]
      }));

      return submitDataFromModalCopy;
    });
  };



  // Location search functions
  const locationTextFieldChangeHandler = (e) => {
    setLocationLoading(true);
    setLocationList([]);
    setLocationSearchText(e.target.value);
  };

  const locationChangeHandler = (e, value) => {
    if (value) {
      const parts = value.split(', ');
      let locationObject = {
        city: "",
        state: "",
        country: ""
      };

      if (parts.length >= 3) {
        locationObject = {
          city: parts[0].trim(),
          state: parts[1].trim(),
          country: parts[2].trim()
        };
      } else if (parts.length === 2) {
        locationObject = {
          city: "",
          state: parts[0].trim(),
          country: parts[1].trim()
        };
      } else if (parts.length === 1) {
        locationObject = {
          city: "",
          state: "",
          country: parts[0].trim()
        };
      }

      handleFieldChange(candidateSubmissionConfig.currentLocation, locationObject);
    } else {
      handleFieldChange(candidateSubmissionConfig.currentLocation, {
        city: "",
        state: "",
        country: ""
      });
    }
    setLocationList([]);
    setLocationSearchText("");
  };

  const getLocationResult = async (searchText) => {
    try {
      let response = await getLocations(searchText);
      response = response?.data?.data;
      let locationArr = [];
      response.forEach((item) => {
        let str = "";
        str += (item.name).trim();
        if (item.state) {
          str += ", " + (item?.state?.name).trim();
        }
        if (item.country) {
          str += ", " + (item?.country?.name).trim();
        }
        locationArr.push(str);
      });
      setLocationList(locationArr);
      setTimeout(() => {
        setLocationLoading(false);
      }, 1000);
    } catch (error) {
      console.log(error);
      setLocationLoading(false);
    }
  };

  // Preferred locations search functions
  const preferredLocationTextFieldChangeHandler = (e) => {
    setPreferredLocationLoading(true);
    setPreferredLocationList([]);
    setPreferredLocationSearchText(e.target.value);
  };

  const preferredLocationChangeHandler = (e, value) => {
    const locationObjects = value.map(locationStr => {
      const parts = locationStr.split(', ');
      if (parts.length >= 3) {
        return {
          city: parts[0] || "",
          state: parts[1] || "",
          country: parts[2] || ""
        };
      } else if (parts.length === 2) {
        return {
          city: "",
          state: parts[0] || "",
          country: parts[1] || ""
        };
      } else {
        return {
          city: "",
          state: "",
          country: parts[0] || ""
        };
      }
    });

    handleFieldChange(candidateSubmissionConfig.preferredLocation, locationObjects);
    setPreferredLocationList([]);
    setPreferredLocationSearchText("");
  };

  const getPreferredLocationResult = async (searchText) => {
    try {
      let response = await getLocations(searchText);
      response = response?.data?.data;
      let locationArr = [];
      response.forEach((item) => {
        let str = "";
        str += (item.name).trim();
        if (item.state) {
          str += ", " + (item?.state?.name).trim();
        }
        if (item.country) {
          str += ", " + (item?.country?.name).trim();
        }
        locationArr.push(str);
      });
      setPreferredLocationList(locationArr);
      setTimeout(() => {
        setPreferredLocationLoading(false);
      }, 1000);
    } catch (error) {
      console.log(error);
      setPreferredLocationLoading(false);
    }
  };

  // Clients worked with handlers
  const handleClientsWorkedWithChange = (e, value) => {
    handleFieldChange(candidateSubmissionConfig.clientsWorkedWith, value);
    setClientsWorkedWithOptions([]);
  };

  const clientsWorkedWithTextFieldHandler = (e) => {
    setCurrentClientsWorkedWithValue(e.target.value);
    let arr = [];
    arr.push(e.target.value);
    setClientsWorkedWithOptions(arr);
  };

  const handleSpocEmailChange = (e, value) => {
    // If current candidate has existing requisition, handle individually
    if (selectedCandidate.submitData?.candidateReqExist) {
      if (value.length > 0) {
        let currentValue = value[value.length - 1] || "";
        let isValid = validateEmail(currentValue);

        if (!isValid && currentValue !== "") {
          NotificationManager.error("Please enter a valid email address", "Invalid Email", 10000);
          setAdditionalSPOCOptions([]);
          return;
        }
      }
      setSubmitDataFromModal(prevSubmitData => {
        const submitDataFromModalCopy = [...prevSubmitData];
        const index = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);
        if (index !== -1) {
          if (!submitDataFromModalCopy[index].candidateSubmissionData) {
            submitDataFromModalCopy[index].candidateSubmissionData = { details: [], altContacts: [] };
          }
          submitDataFromModalCopy[index].candidateSubmissionData.altContacts = value;

          setSelectedCandidate(prevCandidate => ({
            ...prevCandidate,
            submitData: submitDataFromModalCopy[index]
          }));
        }

        return submitDataFromModalCopy;
      });
      setAdditionalSPOCOptions([]);
      return;
    }

    // For candidates without existing requisitions, continue with shared logic
    const previousValue = sharedAdditionalSpoc || [];
    const isRemoval = value.length < previousValue.length;

    if (isRemoval) {
      setSharedAdditionalSpoc(value);

      setSubmitDataFromModal(prevSubmitData => {
        const submitDataFromModalCopy = [...prevSubmitData];

        submitDataFromModalCopy.forEach(candidate => {
          if (candidate.candidateSubmissionData && !candidate.candidateReqExist) {
            candidate.candidateSubmissionData.altContacts = value;
          }
        });
        const currentIndex = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);
        if (currentIndex !== -1 && !selectedCandidate.submitData?.candidateReqExist) {
          setSelectedCandidate(prevCandidate => ({
            ...prevCandidate,
            submitData: submitDataFromModalCopy[currentIndex]
          }));
        }

        return submitDataFromModalCopy;
      });
      setAdditionalSPOCOptions([]);
      return;
    }

    // For additions, validate the new email
    let currentValue = value[value.length - 1] || "";
    let isValid = validateEmail(currentValue);

    if (isValid || currentValue === "") {
      setSharedAdditionalSpoc(value);

      setSubmitDataFromModal(prevSubmitData => {
        const submitDataFromModalCopy = [...prevSubmitData];

        submitDataFromModalCopy.forEach(candidate => {
          if (candidate.candidateSubmissionData && !candidate.candidateReqExist) {
            candidate.candidateSubmissionData.altContacts = value;
          }
        });

        const currentIndex = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);
        if (currentIndex !== -1 && !selectedCandidate.submitData?.candidateReqExist) {
          setSelectedCandidate(prevCandidate => ({
            ...prevCandidate,
            submitData: submitDataFromModalCopy[currentIndex]
          }));
        }

        return submitDataFromModalCopy;
      });
    }
    else {
      NotificationManager.error("Please enter a valid email address", "Invalid Email", 10000);
    }
    setAdditionalSPOCOptions([]);
  };

  const additionalSpocTextFieldHandler = (e) => {
    setCurrentAdditionalSpocValue(e.target.value);
    let arr = [];
    arr.push(e.target.value);
    setAdditionalSPOCOptions(arr);
  };

  // Function to format submission data with proper phone number format
  const formatSubmissionDataForPayload = (submissionData) => {
  if (!submissionData || !submissionData.details) return submissionData;

  const formattedDetails = submissionData.details.map(detail => {
    if (detail.subCode === candidateSubmissionConfig.contactNo) {
      // Use the normalized phone number function
      const normalizePhoneNumber = (phoneValue, countryCode) => {
        if (!phoneValue) return "";
        
        let cleanValue = phoneValue.toString().replace(/[-\s()[\]]/g, "");
        
        if (cleanValue.startsWith('+')) {
          return cleanValue;
        }
        
        const countryCodeDigits = countryCode.replace('+', '');
        
        if (cleanValue.startsWith(countryCodeDigits)) {
          return '+' + cleanValue;
        }
        
        return countryCode + cleanValue;
      };

      const countryCode = selectedCandidate.submitData?.candidateCompleteResume?.countryCallingCode ||
        selectedCandidate.submitData?.countryCallingCode || "+91";
      
      
      return {
        ...detail,
        value: normalizePhoneNumber(detail.value, countryCode)
      };
    }

    if (detail.subCode === candidateSubmissionConfig.totalExperience || detail.subCode === candidateSubmissionConfig.relevantExperience) {
      let years = detail.value?.years ?? detail.value?.year ?? detail.value?.y ?? '';
      let months = detail.value?.months ?? detail.value?.month ?? detail.value?.m ?? '';
      return {
        ...detail,
        value: {
          years: years === '' ? '' : parseInt(years) || 0,
          months: months === '' ? '' : parseInt(months) || 0
        }
      };
    }
    return detail;
  });

  return {
    ...submissionData,
    details: formattedDetails
  };
};


  // Call the formatting function whenever submission data changes
  useEffect(() => {
    if (onFormatSubmissionData && selectedCandidate.submitData?.candidateSubmissionData) {
      const formattedData = formatSubmissionDataForPayload(selectedCandidate.submitData.candidateSubmissionData);
      onFormatSubmissionData(formattedData);
    }
  }, [selectedCandidate.submitData?.candidateSubmissionData, onFormatSubmissionData]);

  useEffect(() => {
    getRequiredFields();
  }, []);

  useEffect(() => {
    setIsInitialized(false);
    if (selectedCandidate.submitData?.candidateReqExist) {
      setDisabledFields(true);
    } else {
      setDisabledFields(false);
    }
  }, [selectedCandidate.submitData?.email, selectedCandidate.submitData?.candidateReqExist]);

  // Location search useEffect
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (locationSearchText.length > 0) {
        setLocationLoading(true);
        getLocationResult(locationSearchText);
      } else {
        setLocationLoading(false);
        setLocationList([]);
      }
    }, 700);

    return () => clearTimeout(timeout);
  }, [locationSearchText]);

  // Preferred location search useEffect
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (preferredLocationSearchText.length > 0) {
        setPreferredLocationLoading(true);
        getPreferredLocationResult(preferredLocationSearchText);
      } else {
        setPreferredLocationLoading(false);
        setPreferredLocationList([]);
      }
    }, 700);

    return () => clearTimeout(timeout);
  }, [preferredLocationSearchText]);

  return (
    <div className="sectionContent_bulkSubmit">
      <div className="candidateSubmissionGrid_bulkSubmit">
        {noActiveConfigMessage ? (
          <div className="fieldContainer_bulkSubmit" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
            <div style={{
              color: (noActiveConfigMessage.includes('PC01 template') || noActiveConfigMessage.includes('PC02 template')) ? '#ff9800' : '#f44336',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: (noActiveConfigMessage.includes('PC01 template') || noActiveConfigMessage.includes('PC02 template')) ? '#fff3e0' : '#ffebee',
              border: (noActiveConfigMessage.includes('PC01 template') || noActiveConfigMessage.includes('PC02 template')) ? '1px solid #ffcc02' : '1px solid #ffcdd2',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '10px'
            }}>
              {noActiveConfigMessage}
            </div>
          </div>
        ) : (
          <>
            {requiredFields.map((field, index) => (
              <RequiredFieldRenderer
                key={field.submissionCode}
                field={field}
                selectedCandidate={selectedCandidate}
                handleFieldChange={handleFieldChange}
                disabledFields={disabledFields}
                locationList={locationList}
                locationLoading={locationLoading}
                locationChangeHandler={locationChangeHandler}
                locationSearchText={locationSearchText}
                locationTextFieldChangeHandler={locationTextFieldChangeHandler}
                preferredLocationList={preferredLocationList}
                preferredLocationLoading={preferredLocationLoading}
                preferredLocationChangeHandler={preferredLocationChangeHandler}
                preferredLocationSearchText={preferredLocationSearchText}
                preferredLocationTextFieldChangeHandler={preferredLocationTextFieldChangeHandler}
                clientsWorkedWithOptions={clientsWorkedWithOptions}
                currentClientsWorkedWithValue={currentClientsWorkedWithValue}
                handleClientsWorkedWithChange={handleClientsWorkedWithChange}
                clientsWorkedWithTextFieldHandler={clientsWorkedWithTextFieldHandler}
                currencyOptions={currencyOptions}
                setSubmitDataFromModal={setSubmitDataFromModal}
                setSelectedCandidate={setSelectedCandidate}
                fieldAltNames={fieldAltNames}
                allowedSubmissionCodes={allowedSubmissionCodes}
              />
            ))}
            <div className="fieldContainer_bulkSubmit" style={{ gridColumn: '1 / -1' }}>
              <label className="fieldLabel_requiredFields">
                Additional SPOC Mail ID{additionalSpocRequired && <span>*</span>}
              </label>
              <Autocomplete
                multiple
                freeSolo
                fullWidth
                options={additionalSpocOptions}
                value={
                  selectedCandidate.submitData?.candidateReqExist
                    ? (selectedCandidate.submitData?.candidateSubmissionData?.altContacts || [])
                    : (sharedAdditionalSpoc || [])
                }
                onChange={handleSpocEmailChange}
                disabled={disabledFields}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    value={currentAdditionalSpocValue}
                    onChange={additionalSpocTextFieldHandler}
                    placeholder={
                      selectedCandidate.submitData?.candidateReqExist
                        ? "Enter email addresses (individual for this candidate)"
                        : "Enter email addresses (shared across all candidates)"
                    }
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubmissionComp;

